import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — Card family. Surface container with the DS card shadow and a
 * single 1px outline (no colored borders). KpiCard composes Card for the
 * dashboard metric tile (icon chip + value + label + optional delta).
 */
export const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-so-lg border border-sober-outline bg-sober-surface shadow-so-card',
        className
      )}
      {...props}
    />
  )
);
Card.displayName = 'Card';

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-1.5 p-5', className)} {...props} />
  )
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn('text-so-16 font-bold text-sober-text', className)} {...props} />
  )
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-so-14 text-sober-secondary', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

export const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-5 pt-0', className)} {...props} />
  )
);
CardContent.displayName = 'CardContent';

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center justify-between p-5 pt-0', className)}
      {...props}
    />
  )
);
CardFooter.displayName = 'CardFooter';

const kpiIconVariants = cva(
  'grid h-10 w-10 shrink-0 place-items-center rounded-so-md sm:h-11 sm:w-11 [&_svg]:size-5',
  {
    variants: {
      iconVariant: {
        blue: 'bg-sober-primary-bg text-sober-primary',
        green: 'bg-sober-green-bg text-sober-green-fg',
        yellow: 'bg-sober-yellow-bg text-sober-yellow-fg',
        red: 'bg-sober-red-bg text-sober-red',
        purple: 'bg-sober-purple-bg text-sober-purple',
      },
    },
    defaultVariants: { iconVariant: 'blue' },
  }
);

export interface KpiDelta {
  value: string;
  direction: 'up' | 'down';
}

export interface KpiCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof kpiIconVariants> {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  delta?: KpiDelta;
}

export const KpiCard = React.forwardRef<HTMLDivElement, KpiCardProps>(
  ({ className, label, value, icon, iconVariant, delta, ...props }, ref) => (
    <Card
      ref={ref}
      className={cn('flex min-w-0 flex-col items-start gap-3 p-4 sm:flex-row sm:p-5', className)}
      {...props}
    >
      {icon ? <span className={cn(kpiIconVariants({ iconVariant }))}>{icon}</span> : null}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="max-w-full truncate text-so-14 text-sober-secondary">{label}</span>
        <span className="max-w-full text-so-20 font-bold leading-7 text-sober-text [overflow-wrap:anywhere] sm:text-so-28 sm:leading-9">
          {value}
        </span>
        {delta ? (
          <span
            className={cn(
              'inline-flex items-center gap-1 text-so-12 font-bold',
              delta.direction === 'up' ? 'text-sober-green-fg' : 'text-sober-red'
            )}
          >
            {delta.value}
          </span>
        ) : null}
      </div>
    </Card>
  )
);
KpiCard.displayName = 'KpiCard';

export { kpiIconVariants };
